import React, { useState, useRef, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { motion, AnimatePresence } from 'framer-motion';
import AILimitModal from '../../components/AILimitModal/AILimitModal';
import AIUsageBanner from '../../components/AIUsageBanner/AIUsageBanner';
import { Input } from 'antd';
import { Send, Copy, Check, User, Sparkles, RotateCcw, StopCircle, Bookmark, Mic, Download } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { resolveUserAvatar } from '../../utils/userAvatar';
import PageAnimationWrapper from '../../components/PageAnimationWrapper';
import toast from 'react-hot-toast';
import { SERVER_URL } from '../../utils/serverConfig';
import VisualizationRenderer from '../../components/AISearchVisualization/AISearchVisualization';
import AskTgVisualAnswer from '../../components/AskTgVisualAnswer/AskTgVisualAnswer';
import jsPDF from 'jspdf';
import moment from 'moment';
import html2canvas from 'html2canvas';

// Logo Icon Component for Bot Avatar
const LogoIcon = ({ size = 32, className = '' }) => {
  return (
    <svg 
      width={size} 
      height={size} 
      viewBox="150 60 500 500" 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path fillRule="evenodd" clipRule="evenodd" d="M358.223 255.521C346.35 236.916 318.205 154.654 314.654 145.22C310.996 130.114 315.29 119.142 327.428 112.357C334.848 108.912 341.527 108.435 347.357 110.979C373.329 120.838 392.357 128.152 404.495 133.082C414.46 137.11 420.237 147.446 419.442 158.1C409.531 214.708 404.601 243.012 404.601 243.012C402.322 252.765 397.658 259.072 390.714 261.934C380.643 267.076 364.583 265.486 358.223 255.521Z" fill="#34A851"/>
      <circle cx="378.945" cy="62.3744" r="20" fill="#34A851"/>
      <path fillRule="evenodd" clipRule="evenodd" d="M429.671 263.895C441.014 244.973 502.181 183.17 509.018 175.75C520.944 165.732 532.658 164.672 544.107 172.516C550.52 177.658 553.859 183.382 554.231 189.743C556.987 217.358 558.895 237.658 559.902 250.697C560.75 261.404 554.072 271.21 544.16 275.238C489.089 291.563 461.527 299.726 461.527 299.726C451.774 302.005 444.035 300.68 438.417 295.75C429.3 289.125 423.629 274.019 429.671 263.895Z" fill="#F2673A"/>
      <circle cx="611.844" cy="196.368" r="15" fill="#F2673A"/>
      <path fillRule="evenodd" clipRule="evenodd" d="M458.19 330.786C480.239 330.839 564.727 351.511 574.533 353.631C589.215 358.719 596.159 368.207 595.311 382.094C594.198 390.203 591.017 396.034 585.717 399.585C563.455 416.122 546.971 428.207 536.317 435.734C527.572 441.935 515.752 441.246 507.218 434.833C464.921 395.981 443.773 376.528 443.773 376.528C436.776 369.32 433.967 362.005 435.292 354.638C436.352 343.454 446.37 330.786 458.19 330.786Z" fill="#366EB6"/>
      <circle cx="609.676" cy="452.43" r="15" fill="#366EB6"/>
      <path fillRule="evenodd" clipRule="evenodd" d="M415.043 391.741C426.545 410.557 452.941 493.455 456.281 502.943C459.62 518.155 455.114 529.021 442.871 535.54C435.344 538.826 428.718 539.144 422.941 536.494C397.181 526.105 378.312 518.367 366.28 513.226C356.421 508.985 350.856 498.543 351.863 487.943C362.941 431.547 368.453 403.401 368.453 403.401C370.944 393.702 375.715 387.447 382.711 384.744C392.888 379.709 408.895 381.67 415.043 391.741Z" fill="#6C59A6"/>
      <circle cx="390.346" cy="584.463" r="15" fill="#6C59A6"/>
      <path fillRule="evenodd" clipRule="evenodd" d="M339.246 383.684C328.646 403.031 269.97 467.218 263.398 474.851C251.896 485.293 240.235 486.83 228.468 479.462C221.896 474.586 218.292 468.967 217.709 462.66C213.892 435.204 211.189 414.956 209.652 401.97C208.38 391.317 214.687 381.246 224.387 376.847C278.769 358.348 305.96 349.126 305.96 349.126C315.607 346.475 323.398 347.482 329.229 352.2C338.61 358.454 344.918 373.348 339.246 383.684Z" fill="#469FD8"/>
      <circle cx="159.882" cy="458.26" r="15" fill="#469FD8"/>
      <path fillRule="evenodd" clipRule="evenodd" d="M303.416 318.542C281.419 317.482 197.991 292.782 188.239 290.185C173.822 284.408 167.302 274.602 168.839 260.768C170.323 252.711 173.822 247.04 179.281 243.701C202.338 228.224 219.352 216.987 230.377 209.937C239.387 204.16 251.207 205.432 259.423 212.216C299.812 253.029 320.059 273.489 320.059 273.489C326.685 281.015 329.176 288.436 327.48 295.75C325.837 306.934 315.236 319.125 303.416 318.542Z" fill="#2DBDB8"/>
      <circle cx="157.923" cy="189.849" r="15" fill="#2DBDB8"/>
    </svg>
  );
};

const { TextArea } = Input;

const AISearch = ({ visualAnswers = false } = {}) => {
  const { isDark } = useTheme();
  const { user } = useAuth();
  const [inputValue, setInputValue] = useState('');
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showAILimitModal, setShowAILimitModal] = useState(false);
  const [searchStatus, setSearchStatus] = useState('');
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [visualizations, setVisualizations] = useState({});
  const [actionButtons, setActionButtons] = useState({});
  const [pdfMetadata, setPdfMetadata] = useState({});
  const [hoveredMessageId, setHoveredMessageId] = useState(null);
  const [savedMessageIds, setSavedMessageIds] = useState(new Set());
  const [savingMessageId, setSavingMessageId] = useState(null);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const textareaRef = useRef(null);
  const abortControllerRef = useRef(null);
  
  // Voice recognition states
  const [isListening, setIsListening] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recognitionSupported, setRecognitionSupported] = useState(false);
  const recognitionRef = useRef(null);

  // Auto-scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Initialize Speech Recognition API
  useEffect(() => {
    // Check if browser supports Speech Recognition
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (SpeechRecognition) {
      setRecognitionSupported(true);
      const recognition = new SpeechRecognition();
      
      // Configure recognition
      recognition.continuous = true; // Keep listening until manually stopped
      recognition.interimResults = true; // Show interim results
      recognition.lang = 'en-US'; // Language
      
      // Event handlers
      recognition.onstart = () => {
        setIsListening(true);
        setIsTranscribing(false);
      };
      
      recognition.onresult = (event) => {
        // Build complete transcript from all results
        let fullTranscript = '';
        let hasInterim = false;
        
        // Process all results from the beginning
        for (let i = 0; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            fullTranscript += transcript + ' ';
          } else {
            // Interim results - use the latest one (it contains all interim text so far)
            fullTranscript += transcript;
            hasInterim = true;
          }
        }
        
        // Update input field - replace everything with the complete transcript
        if (fullTranscript.trim()) {
          setInputValue(fullTranscript.trim() + (hasInterim ? ' [Listening...]' : ''));
        }
      };
      
      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        setIsTranscribing(false);
        
        // Handle different error types
        if (event.error === 'no-speech') {
          toast.error('No speech detected. Please try again.');
        } else if (event.error === 'audio-capture') {
          toast.error('No microphone found. Please check your microphone.');
        } else if (event.error === 'not-allowed') {
          toast.error('Microphone permission denied. Please allow microphone access.');
        } else {
          toast.error('Speech recognition failed. Please try again.');
        }
        
        // Clear any interim text
        setInputValue(prev => prev.replace(/\s*\[Listening\.\.\.\]\s*$/, ''));
      };
      
      recognition.onend = () => {
        setIsListening(false);
        setIsTranscribing(false);
        // Clear interim text marker
        setInputValue(prev => prev.replace(/\s*\[Listening\.\.\.\]\s*$/, ''));
      };
      
      recognitionRef.current = recognition;
      
      // Cleanup on unmount
      return () => {
        if (recognitionRef.current) {
          recognitionRef.current.stop();
        }
      };
    } else {
      setRecognitionSupported(false);
    }
  }, []);

  // Helper function to adjust insertPosition to word boundary (prevents word cutting)
  const adjustToWordBoundary = (text, position) => {
    if (!text || position <= 0 || position >= text.length) return position;
    
    const charBefore = text[position - 1];
    const charAt = text[position];
    
    // If we're in the middle of a word (alphanumeric before AND after)
    if (/[a-zA-Z0-9]/.test(charBefore) && /[a-zA-Z0-9]/.test(charAt)) {
      // Move forward to end of word
      for (let i = position; i < text.length; i++) {
        if (!/[a-zA-Z0-9]/.test(text[i])) {
          return i;
        }
      }
      return text.length;
    }
    
    return position; // Already at word boundary
  };

  // Handle copy message
  const handleCopyMessage = (messageId, text) => {
    let cleanText = text;
    
    // Remove visualization tags more carefully to preserve word boundaries
    cleanText = cleanText.replace(/<VISUALIZATION>[\s\S]*?<\/VISUALIZATION>/gi, (match, offset, string) => {
      const before = string.substring(Math.max(0, offset - 1), offset);
      const after = string.substring(offset + match.length, offset + match.length + 1);
      const isBreakingWord = /[a-zA-Z0-9]/.test(before) && /[a-zA-Z0-9]/.test(after);
      const hasSpaceBefore = /\s/.test(before);
      const hasSpaceAfter = /\s/.test(after);
      
      if (isBreakingWord && !hasSpaceBefore && !hasSpaceAfter) {
        return ' ';
      }
      return '';
    });
    
    cleanText = cleanText.replace(/\{\s*"type"\s*:\s*"(line|bar|pie|table)"[\s\S]{0,15000}?\}/gi, (match, offset, string) => {
      const before = string.substring(Math.max(0, offset - 1), offset);
      const after = string.substring(offset + match.length, offset + match.length + 1);
      const isBreakingWord = /[a-zA-Z0-9]/.test(before) && /[a-zA-Z0-9]/.test(after);
      const hasSpaceBefore = /\s/.test(before);
      const hasSpaceAfter = /\s/.test(after);
      
      if (isBreakingWord && !hasSpaceBefore && !hasSpaceAfter) {
        return ' ';
      }
      return '';
    });
    
    cleanText = cleanText
      .replace(/<[^>]*>/g, '')
      .replace(/\s{3,}/g, ' ') // Replace 3+ spaces with single space
      .trim();
    
    navigator.clipboard.writeText(cleanText);
    setCopiedMessageId(messageId);
    toast.success('Copied to clipboard!', { duration: 2000 });
    setTimeout(() => setCopiedMessageId(null), 2000);
  };

  // Handle PDF download
  const handleDownloadPDF = async (message) => {
    try {
      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      let yPosition = margin;

      // TeamGrid LogoIcon SVG (just the icon, no text)
      const logoIconSvg = `<svg width="100" height="100" viewBox="150 60 500 500" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" clipRule="evenodd" d="M358.223 255.521C346.35 236.916 318.205 154.654 314.654 145.22C310.996 130.114 315.29 119.142 327.428 112.357C334.848 108.912 341.527 108.435 347.357 110.979C373.329 120.838 392.357 128.152 404.495 133.082C414.46 137.11 420.237 147.446 419.442 158.1C409.531 214.708 404.601 243.012 404.601 243.012C402.322 252.765 397.658 259.072 390.714 261.934C380.643 267.076 364.583 265.486 358.223 255.521Z" fill="#34A851"/>
        <circle cx="378.945" cy="62.3744" r="20" fill="#34A851"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M429.671 263.895C441.014 244.973 502.181 183.17 509.018 175.75C520.944 165.732 532.658 164.672 544.107 172.516C550.52 177.658 553.859 183.382 554.231 189.743C556.987 217.358 558.895 237.658 559.902 250.697C560.75 261.404 554.072 271.21 544.16 275.238C489.089 291.563 461.527 299.726 461.527 299.726C451.774 302.005 444.035 300.68 438.417 295.75C429.3 289.125 423.629 274.019 429.671 263.895Z" fill="#F2673A"/>
        <circle cx="611.844" cy="196.368" r="15" fill="#F2673A"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M458.19 330.786C480.239 330.839 564.727 351.511 574.533 353.631C589.215 358.719 596.159 368.207 595.311 382.094C594.198 390.203 591.017 396.034 585.717 399.585C563.455 416.122 546.971 428.207 536.317 435.734C527.572 441.935 515.752 441.246 507.218 434.833C464.921 395.981 443.773 376.528 443.773 376.528C436.776 369.32 433.967 362.005 435.292 354.638C436.352 343.454 446.37 330.786 458.19 330.786Z" fill="#366EB6"/>
        <circle cx="609.676" cy="452.43" r="15" fill="#366EB6"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M415.043 391.741C426.545 410.557 452.941 493.455 456.281 502.943C459.62 518.155 455.114 529.021 442.871 535.54C435.344 538.826 428.718 539.144 422.941 536.494C397.181 526.105 378.312 518.367 366.28 513.226C356.421 508.985 350.856 498.543 351.863 487.943C362.941 431.547 368.453 403.401 368.453 403.401C370.944 393.702 375.715 387.447 382.711 384.744C392.888 379.709 408.895 381.67 415.043 391.741Z" fill="#6C59A6"/>
        <circle cx="390.346" cy="584.463" r="15" fill="#6C59A6"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M339.246 383.684C328.646 403.031 269.97 467.218 263.398 474.851C251.896 485.293 240.235 486.83 228.468 479.462C221.896 474.586 218.292 468.967 217.709 462.66C213.892 435.204 211.189 414.956 209.652 401.97C208.38 391.317 214.687 381.246 224.387 376.847C278.769 358.348 305.96 349.126 305.96 349.126C315.607 346.475 323.398 347.482 329.229 352.2C338.61 358.454 344.918 373.348 339.246 383.684Z" fill="#469FD8"/>
        <circle cx="159.882" cy="458.26" r="15" fill="#469FD8"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M303.416 318.542C281.419 317.482 197.991 292.782 188.239 290.185C173.822 284.408 167.302 274.602 168.839 260.768C170.323 252.711 173.822 247.04 179.281 243.701C202.338 228.224 219.352 216.987 230.377 209.937C239.387 204.16 251.207 205.432 259.423 212.216C299.812 253.029 320.059 273.489 320.059 273.489C326.685 281.015 329.176 288.436 327.48 295.75C325.837 306.934 315.236 319.125 303.416 318.542Z" fill="#2DBDB8"/>
        <circle cx="157.923" cy="189.849" r="15" fill="#2DBDB8"/>
      </svg>`;

      // Load TeamGrid logo for adding to PDF
      let logoData = null;
      let logoWidth = 0;
      let logoHeight = 0;
      
      try {
        // Convert SVG to base64 data URL
        const logoSrc = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(logoIconSvg)));
        
        // Create image element and convert to canvas for jsPDF compatibility
        const img = new Image();
        await new Promise((resolve, reject) => {
          img.onload = () => {
            try {
              // Create canvas to convert SVG to PNG
              const canvas = document.createElement('canvas');
              canvas.width = img.width || 100;
              canvas.height = img.height || 100;
              const ctx = canvas.getContext('2d');
              ctx.drawImage(img, 0, 0);
              
              // Convert canvas to data URL (PNG format)
              logoData = canvas.toDataURL('image/png');
              
              // Calculate logo size (25mm width, maintain aspect ratio)
              logoWidth = 25;
              logoHeight = (img.height / img.width) * logoWidth;
              
              resolve();
            } catch (canvasError) {
              console.warn('Canvas conversion error:', canvasError);
              resolve(); // Continue without logo
            }
          };
          img.onerror = (error) => {
            console.warn('Could not load logo:', error);
            resolve(); // Continue without logo
          };
          img.src = logoSrc;
        });
      } catch (logoError) {
        console.warn('Logo loading error:', logoError);
        // Continue without logo if it fails
      }

      // Function to add logo to current page (top left, small size)
      const addLogoToPage = () => {
        if (logoData && logoWidth > 0) {
          try {
            // Small logo in top left corner, won't interfere with content
            const smallLogoWidth = 15; // Very small size
            const smallLogoHeight = (logoHeight / logoWidth) * smallLogoWidth;
            pdf.addImage(logoData, 'PNG', margin, 8, smallLogoWidth, smallLogoHeight); // Top left, 8mm from top
          } catch (imgError) {
            console.warn('Error adding logo to PDF:', imgError);
            // Continue without logo
          }
        }
      };

      // Add logo to first page at the top left (small, out of the way)
      addLogoToPage();
      
      // Start title with proper spacing (logo is small, so content can start normally)
      yPosition = margin + 8; // Small offset for logo

      // Title - Capitalize first letter of every word (Title Case) with wrapping
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      const title = pdfMetadata[message.id]?.query || 'Productivity Report';
      // Capitalize first letter of every word
      const titleCase = title.split(' ').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      ).join(' ');
      // Position title to the right of logo (or center if no logo)
      const titleX = logoData ? margin + 20 : margin; // Leave space for logo
      const titleMaxWidth = pageWidth - titleX - margin;
      // Split title into multiple lines if it's too long
      const titleLines = pdf.splitTextToSize(titleCase, titleMaxWidth);
      titleLines.forEach((titleLine, index) => {
        if (yPosition > pageHeight - 30) {
          pdf.addPage();
          addLogoToPage();
          yPosition = margin + 8;
        }
        pdf.text(titleLine, titleX, yPosition);
        yPosition += 7; // Smaller spacing between title lines
      });
      yPosition += 8; // Extra space after title

      // Generated date
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Generated on: ${moment().format('MMMM D, YYYY HH:mm')}`, margin, yPosition);
      yPosition += 15;

      // Get visualization data for this message
      const messageVisualizations = visualizations[message.id] || [];
      
      // Helper function to render chart as image in PDF
      const renderChartAsImage = async (vizData, pdf, startY, maxWidth, margin, pageHeight, isDark) => {
        try {
          // Charts use ResponsiveContainer with height={300}, plus title and padding
          const chartWidth = maxWidth;
          const chartHeight = 300; // Chart content height (matches ResponsiveContainer)
          const titleHeight = vizData.title ? 50 : 0;
          const padding = 16; // Top and bottom padding
          const containerHeight = titleHeight + chartHeight + padding;
          
          // Create a temporary container for the chart
          const tempContainer = document.createElement('div');
          tempContainer.style.position = 'absolute';
          tempContainer.style.left = '-9999px';
          tempContainer.style.top = '0';
          tempContainer.style.width = `${chartWidth}px`;
          tempContainer.style.height = `${containerHeight}px`;
          tempContainer.style.backgroundColor = isDark ? '#1a1a1a' : '#ffffff';
          tempContainer.style.padding = '8px';
          tempContainer.style.boxSizing = 'border-box';
          tempContainer.style.overflow = 'hidden';
          document.body.appendChild(tempContainer);
          
          // Create root and render chart component
          const root = createRoot(tempContainer);
          root.render(
            React.createElement(VisualizationRenderer, { 
              data: vizData, 
              isDark: isDark || false 
            })
          );
          
          // Wait for React to render and charts to initialize
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // Capture as canvas using html2canvas
          const canvas = await html2canvas(tempContainer, {
            backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
            scale: 1,
            logging: false,
            useCORS: true,
            allowTaint: true,
            width: chartWidth,
            height: containerHeight
          });
          
          // Clean up
          root.unmount();
          document.body.removeChild(tempContainer);
          
          // Convert canvas to image data
          const imgData = canvas.toDataURL('image/png');
          
          // Use full width but scale height appropriately to fit page
          const maxChartHeight = pageHeight - margin * 2 - 40;
          const imgWidth = chartWidth; // Use full width, no scaling
          
          // Calculate height maintaining aspect ratio, but limit to max height
          let imgHeight = (containerHeight / chartWidth) * imgWidth;
          
          // If height is too large, scale it down while maintaining aspect ratio
          if (imgHeight > maxChartHeight) {
            const heightScaleFactor = maxChartHeight / imgHeight;
            imgHeight = maxChartHeight;
            // Don't scale width down, just limit height
          }
          
          // Check if we need a new page
          let currentY = startY;
          if (currentY + imgHeight > pageHeight - margin) {
            pdf.addPage();
            addLogoToPage();
            currentY = margin + 8;
          }
          
          // Add image to PDF with full width and calculated height
          pdf.addImage(imgData, 'PNG', margin, currentY, imgWidth, imgHeight);
          
          return currentY + imgHeight + 10; // Return new Y position with spacing
        } catch (error) {
          console.error('Error rendering chart as image:', error);
          console.error('Error details:', {
            message: error.message,
            stack: error.stack,
            vizData: vizData
          });
          // Fallback: render as text
          return startY;
        }
      };
      
      // Helper function to render table in PDF
      const renderTable = (vizData, pdf, startY, maxWidth, margin, pageHeight) => {
        let currentY = startY;
        const tableMargin = margin;
        const availableWidth = maxWidth;
        
        // Table title
        if (vizData.title) {
          pdf.setFontSize(12);
          pdf.setFont('helvetica', 'bold');
          const titleLines = pdf.splitTextToSize(vizData.title, availableWidth);
          titleLines.forEach(titleLine => {
            if (currentY > pageHeight - 40) {
              pdf.addPage();
              addLogoToPage();
              currentY = margin + 8;
            }
            pdf.text(titleLine, tableMargin, currentY);
            currentY += 6;
          });
          currentY += 4;
        }
        
        if (!vizData.data || !Array.isArray(vizData.data) || vizData.data.length === 0) {
          return currentY;
        }
        
        // Get columns from visualization or infer from first data item
        const columns = vizData.columns || (vizData.data[0] ? Object.keys(vizData.data[0]) : []);
        if (columns.length === 0) {
          return currentY;
        }
        
        // Calculate column widths (distribute evenly with some padding)
        const colCount = columns.length;
        const cellPadding = 3;
        const colWidth = (availableWidth - (colCount + 1) * cellPadding) / colCount;
        
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'bold');
        
        // Draw header row
        let xPos = tableMargin + cellPadding;
        columns.forEach((col, colIndex) => {
          if (currentY > pageHeight - 40) {
            pdf.addPage();
            addLogoToPage();
            currentY = margin + 8;
          }
          const headerText = String(col);
          const headerLines = pdf.splitTextToSize(headerText, colWidth - 2);
          headerLines.forEach((line, lineIdx) => {
            if (lineIdx === 0) {
              pdf.text(line, xPos, currentY);
            } else {
              currentY += 4;
              pdf.text(line, xPos, currentY);
            }
          });
          xPos += colWidth + cellPadding;
        });
        currentY += 8;
        
        // Draw data rows
        pdf.setFont('helvetica', 'normal');
        vizData.data.forEach((row, rowIndex) => {
          if (currentY > pageHeight - 40) {
            pdf.addPage();
            addLogoToPage();
            currentY = margin + 8;
            
            // Redraw header on new page
            pdf.setFont('helvetica', 'bold');
            xPos = tableMargin + cellPadding;
            columns.forEach(col => {
              const headerText = String(col);
              const headerLines = pdf.splitTextToSize(headerText, colWidth - 2);
              headerLines.forEach((line, lineIdx) => {
                if (lineIdx === 0) {
                  pdf.text(line, xPos, currentY);
                } else {
                  currentY += 4;
                  pdf.text(line, xPos, currentY);
                }
              });
              xPos += colWidth + cellPadding;
            });
            currentY += 8;
            pdf.setFont('helvetica', 'normal');
          }
          
          let rowStartY = currentY;
          let maxLinesInRow = 1;
          
          // First pass: calculate max lines needed for this row
          xPos = tableMargin + cellPadding;
          columns.forEach(col => {
            const cellValue = row[col] !== undefined && row[col] !== null ? String(row[col]) : '';
            const cellLines = pdf.splitTextToSize(cellValue, colWidth - 2);
            maxLinesInRow = Math.max(maxLinesInRow, cellLines.length);
            xPos += colWidth + cellPadding;
          });
          
          // Second pass: render cells
          xPos = tableMargin + cellPadding;
          columns.forEach((col, colIndex) => {
            const cellValue = row[col] !== undefined && row[col] !== null ? String(row[col]) : '';
            const cellLines = pdf.splitTextToSize(cellValue, colWidth - 2);
            cellLines.forEach((line, lineIdx) => {
              const yOffset = lineIdx * 4;
              pdf.text(line, xPos, currentY + yOffset);
            });
            xPos += colWidth + cellPadding;
          });
          
          currentY += maxLinesInRow * 4 + 2; // Add spacing between rows
        });
        
        return currentY + 8; // Extra space after table
      };
      
      // Message content - preserve original text properly with visualizations in correct order
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      
      // Get original text - we need this to calculate insertion points
      const originalText = message.text || '';
      
      // Helper function to clean text (remove visualization tags and JSON, keep content)
      const removeJsonVisualizations = (text) => {
        if (!text) return text;
        let cleaned = text;
        
        // Remove VISUALIZATION tags more carefully to preserve word boundaries
        cleaned = cleaned.replace(/<VISUALIZATION>[\s\S]*?<\/VISUALIZATION>/gi, (match, offset, string) => {
          // Check if we're breaking a word
          const before = string.substring(Math.max(0, offset - 1), offset);
          const after = string.substring(offset + match.length, offset + match.length + 1);
          
          // If breaking a word (alphanumeric before and after), add space
          const isBreakingWord = /[a-zA-Z0-9]/.test(before) && /[a-zA-Z0-9]/.test(after);
          const hasSpaceBefore = /\s/.test(before);
          const hasSpaceAfter = /\s/.test(after);
          
          if (isBreakingWord && !hasSpaceBefore && !hasSpaceAfter) {
            return ' '; // Add space to prevent word breaking
          }
          
          return ''; // Remove visualization tag
        });
        
        // Remove JSON visualization objects (aggressive removal with brace counting)
        let maxPasses = 5;
        for (let pass = 0; pass < maxPasses; pass++) {
          const before = cleaned;
          // Match JSON objects that look like visualizations
          cleaned = cleaned.replace(/\{\s*"type"\s*:\s*"(line|bar|pie|table)"[\s\S]{0,50000}?\}/gi, (match, offset, string) => {
            // Check if breaking a word
            const before = string.substring(Math.max(0, offset - 1), offset);
            const after = string.substring(offset + match.length, offset + match.length + 1);
            const isBreakingWord = /[a-zA-Z0-9]/.test(before) && /[a-zA-Z0-9]/.test(after);
            const hasSpaceBefore = /\s/.test(before);
            const hasSpaceAfter = /\s/.test(after);
            
            if (isBreakingWord && !hasSpaceBefore && !hasSpaceAfter) {
              return ' ';
            }
            
            return '';
          });
          if (before === cleaned) break; // No more matches
        }
        
        // Clean up extra whitespace
        cleaned = cleaned.replace(/\n\s*\n\s*\n+/g, '\n\n');
        cleaned = cleaned.replace(/\s{3,}/g, ' '); // Replace 3+ spaces with single space
        
        return cleaned.trim();
      };
      
      // Create segments: interleave text and visualizations based on insertPosition
      const segments = [];
      
      if (messageVisualizations && messageVisualizations.length > 0) {
        // Sort visualizations by insertPosition
        const sortedVizList = [...messageVisualizations].sort((a, b) => (a.insertPosition || 0) - (b.insertPosition || 0));
        
        let lastIndex = 0;
        
        sortedVizList.forEach((viz) => {
          const originalPos = viz.insertPosition || 0;
          // Clamp position to valid range
          const clampedPos = Math.min(Math.max(0, originalPos), originalText.length);
          // Adjust to word boundary to prevent word cutting
          const adjustedPos = adjustToWordBoundary(originalText, clampedPos);
          
          // Get text before this visualization
          const textBeforeViz = originalText.substring(lastIndex, adjustedPos);
          const cleanedTextBeforeViz = removeJsonVisualizations(textBeforeViz);
          
          if (cleanedTextBeforeViz.trim()) {
            segments.push({
              type: 'text',
              content: cleanedTextBeforeViz
            });
          }
          
          // Add visualization
          segments.push({
            type: 'visualization',
            data: viz
          });
          
          lastIndex = adjustedPos;
        });
        
        // Add remaining text after last visualization
        const textAfterLastViz = originalText.substring(lastIndex);
        const cleanedTextAfterLastViz = removeJsonVisualizations(textAfterLastViz);
        if (cleanedTextAfterLastViz.trim()) {
          segments.push({
            type: 'text',
            content: cleanedTextAfterLastViz
          });
        }
      } else {
        // No visualizations, just add all text
        const cleanedText = removeJsonVisualizations(originalText);
        if (cleanedText.trim()) {
          segments.push({
            type: 'text',
            content: cleanedText
          });
        }
      }
      
      // Helper function to clean text for PDF rendering
      const cleanTextForPDF = (text) => {
      // Create a temporary div to properly extract text from HTML
      const tempDiv = document.createElement('div');
        tempDiv.innerHTML = text;
      
      // Get text content - preserve markdown bold markers
        let rawText = tempDiv.innerHTML || text;
      
      // Remove HTML tags but keep markdown bold (**text**)
        let clean = rawText
        .replace(/<[^>]+>/g, '') // Remove HTML tags
        .replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
        .replace(/&amp;/g, '&') // Replace &amp; with &
        .replace(/&lt;/g, '<') // Replace &lt; with <
        .replace(/&gt;/g, '>') // Replace &gt; with >
        .replace(/&quot;/g, '"') // Replace &quot; with "
        .trim();
      
      // Remove emojis using regex (removes emoji Unicode ranges)
        clean = clean
        .replace(/[\u{1F600}-\u{1F64F}]/gu, '') // Emoticons
        .replace(/[\u{1F300}-\u{1F5FF}]/gu, '') // Misc Symbols and Pictographs
        .replace(/[\u{1F680}-\u{1F6FF}]/gu, '') // Transport and Map
        .replace(/[\u{1F1E0}-\u{1F1FF}]/gu, '') // Flags
        .replace(/[\u{2600}-\u{26FF}]/gu, '') // Misc symbols
        .replace(/[\u{2700}-\u{27BF}]/gu, '') // Dingbats
        .replace(/[\u{1F900}-\u{1F9FF}]/gu, '') // Supplemental Symbols and Pictographs
        .replace(/[\u{1FA00}-\u{1FA6F}]/gu, '') // Chess Symbols
        .replace(/[\u{1FA70}-\u{1FAFF}]/gu, '') // Symbols and Pictographs Extended-A
        .replace(/[\u{FE00}-\u{FE0F}]/gu, '') // Variation Selectors
        .replace(/[\u{200D}]/gu, '') // Zero Width Joiner
        .replace(/[\u{200B}]/gu, '') // Zero Width Space
        .replace(/\n{3,}/g, '\n\n'); // Max 2 consecutive newlines
      
        return clean;
      };
      
      // Helper function to render text content
      const renderTextContent = (textContent) => {
        const cleanText = cleanTextForPDF(textContent);
      const lines = cleanText.split('\n');
      const maxWidth = pageWidth - 2 * margin;
      
      lines.forEach((line) => {
        // Check if we need a new page
        if (yPosition > pageHeight - 30) {
          pdf.addPage();
          addLogoToPage();
          yPosition = margin + 8;
        }
        
        // Handle empty lines
        if (line.trim() === '') {
          yPosition += 4;
          return;
        }
        
        // Check if line contains markdown bold (**text**)
        if (line.includes('**')) {
          // Split line by bold markers
          const parts = line.split(/(\*\*[^*]+\*\*)/g);
          let currentX = margin;
          
          parts.forEach((part) => {
            if (!part) return;
            
            // Check if this part is bold
            if (part.startsWith('**') && part.endsWith('**')) {
              // Remove ** and render as bold
              const boldText = part.slice(2, -2);
              pdf.setFont('helvetica', 'bold');
              
              // Calculate if text fits on current line
              const textWidth = pdf.getTextWidth(boldText);
              if (currentX + textWidth > pageWidth - margin) {
                yPosition += 6;
                currentX = margin;
                if (yPosition > pageHeight - 30) {
                  pdf.addPage();
                  addLogoToPage();
                  yPosition = margin + 8;
                }
              }
              
              pdf.text(boldText, currentX, yPosition);
              currentX += textWidth;
            } else {
              // Regular text
              pdf.setFont('helvetica', 'normal');
              
              // Split text if needed
              const textLines = pdf.splitTextToSize(part, maxWidth - (currentX - margin));
              textLines.forEach((textLine, lineIndex) => {
                if (lineIndex > 0) {
                  yPosition += 6;
                  currentX = margin;
                  if (yPosition > pageHeight - 30) {
                    pdf.addPage();
                    addLogoToPage();
                    yPosition = margin + 8;
                  }
                }
                pdf.text(textLine, currentX, yPosition);
                currentX += pdf.getTextWidth(textLine);
              });
            }
          });
          
          yPosition += 6; // Move to next line after processing
        } else {
          // No bold markers, render normally
          pdf.setFont('helvetica', 'normal');
          const textLines = pdf.splitTextToSize(line, maxWidth);
          textLines.forEach((textLine) => {
            if (yPosition > pageHeight - 30) {
              pdf.addPage();
              addLogoToPage();
              yPosition = margin + 8;
            }
            pdf.text(textLine, margin, yPosition);
            yPosition += 6;
          });
        }
      });
      };
      
      const maxWidth = pageWidth - 2 * margin;
      
      // Render segments in order (text and visualizations interleaved)
      // Use for...of loop to support async/await for chart rendering
      for (const segment of segments) {
        if (segment.type === 'text') {
          renderTextContent(segment.content);
        } else if (segment.type === 'visualization') {
          const viz = segment.data;
          
          // Debug: log visualization data
          console.log('📊 [PDF] Rendering visualization:', {
            chartType: viz.chartType || viz.type,
            hasTitle: !!viz.title,
            hasData: !!(viz.data && Array.isArray(viz.data)),
            dataLength: viz.data?.length || 0,
            hasColumns: !!(viz.columns && Array.isArray(viz.columns))
          });
          
          yPosition += 10; // Extra space before visualization
          
          if (yPosition > pageHeight - 60) {
            pdf.addPage();
            addLogoToPage();
            yPosition = margin + 8;
          }
          
          // Get chart type (could be chartType or type property)
          const chartType = viz.chartType || viz.type || 'table';
          
          // Render table visualization as formatted table
          if (chartType === 'table' && viz.data && Array.isArray(viz.data) && viz.data.length > 0) {
            yPosition = renderTable(viz, pdf, yPosition, maxWidth, margin, pageHeight);
          } 
          // Render other chart types (line, bar, pie) as actual images
          else if (viz.data && Array.isArray(viz.data) && viz.data.length > 0 && (chartType === 'line' || chartType === 'bar' || chartType === 'pie')) {
            // Render chart as image (use light theme for PDF)
            yPosition = await renderChartAsImage(viz, pdf, yPosition, maxWidth, margin, pageHeight, false);
          }
          // Fallback: render as text if it's not a known chart type or has no data
          else if (viz.data && Array.isArray(viz.data) && viz.data.length > 0) {
            // Render title if available
            if (viz.title) {
              pdf.setFontSize(12);
              pdf.setFont('helvetica', 'bold');
              const titleLines = pdf.splitTextToSize(viz.title, maxWidth);
              titleLines.forEach(titleLine => {
                if (yPosition > pageHeight - 30) {
                  pdf.addPage();
                  addLogoToPage();
                  yPosition = margin + 8;
                }
                pdf.text(titleLine, margin, yPosition);
                yPosition += 6;
              });
              yPosition += 4;
            }
            
            // For other types, try to render as table if we have structured data
            if (viz.columns && Array.isArray(viz.columns) && viz.columns.length > 0) {
              const tableViz = {
                ...viz,
                chartType: 'table'
              };
              yPosition = renderTable(tableViz, pdf, yPosition, maxWidth, margin, pageHeight);
            } else {
              // Render data as formatted text list
              pdf.setFontSize(10);
              pdf.setFont('helvetica', 'normal');
              
              viz.data.slice(0, 50).forEach((item, index) => {
                if (yPosition > pageHeight - 30) {
                  pdf.addPage();
                  addLogoToPage();
                  yPosition = margin + 8;
                }
                
                let itemText = '';
                if (typeof item === 'object' && item !== null) {
                  const entries = Object.entries(item);
                  itemText = entries.map(([key, value]) => `${key}: ${value}`).join(', ');
                } else {
                  itemText = String(item);
                }
                
                const itemLines = pdf.splitTextToSize(itemText, maxWidth);
                itemLines.forEach(itemLine => {
                  if (yPosition > pageHeight - 30) {
                    pdf.addPage();
                    addLogoToPage();
                    yPosition = margin + 8;
                  }
                  pdf.text(itemLine, margin, yPosition);
                  yPosition += 5;
                });
                yPosition += 2;
              });
              
              if (viz.data.length > 50) {
                pdf.text(`... and ${viz.data.length - 50} more items`, margin, yPosition);
                yPosition += 5;
              }
            }
            
            yPosition += 8;
          }
          // If visualization has no data, just render title
          else if (viz.title) {
            pdf.setFontSize(12);
            pdf.setFont('helvetica', 'bold');
            const titleLines = pdf.splitTextToSize(viz.title, maxWidth);
            titleLines.forEach(titleLine => {
              if (yPosition > pageHeight - 30) {
                pdf.addPage();
                addLogoToPage();
                yPosition = margin + 8;
              }
              pdf.text(titleLine, margin, yPosition);
              yPosition += 6;
            });
            yPosition += 8;
          }
        }
      }

      // Save the PDF
      const fileName = `Report_${moment().format('YYYY-MM-DD_HH-mm')}.pdf`;
      pdf.save(fileName);
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      toast.error(`Failed to generate PDF: ${error.message || 'Unknown error'}`);
    }
  };

  // Handle save summary
  const handleSaveSummary = async (message) => {
    if (savedMessageIds.has(message.id)) {
      toast.info('Summary already saved!', { duration: 2000 });
      return;
    }

    setSavingMessageId(message.id);

    try {
      // Clean the summary text
      let cleanSummary = message.text;
      
      // Remove visualization tags more carefully to preserve word boundaries
      cleanSummary = cleanSummary.replace(/<VISUALIZATION>[\s\S]*?<\/VISUALIZATION>/gi, (match, offset, string) => {
        const before = string.substring(Math.max(0, offset - 1), offset);
        const after = string.substring(offset + match.length, offset + match.length + 1);
        const isBreakingWord = /[a-zA-Z0-9]/.test(before) && /[a-zA-Z0-9]/.test(after);
        const hasSpaceBefore = /\s/.test(before);
        const hasSpaceAfter = /\s/.test(after);
        
        if (isBreakingWord && !hasSpaceBefore && !hasSpaceAfter) {
          return ' ';
        }
        return '';
      });
      
      cleanSummary = cleanSummary.replace(/\{\s*"type"\s*:\s*"(line|bar|pie|table)"[\s\S]{0,15000}?\}/gi, (match, offset, string) => {
        const before = string.substring(Math.max(0, offset - 1), offset);
        const after = string.substring(offset + match.length, offset + match.length + 1);
        const isBreakingWord = /[a-zA-Z0-9]/.test(before) && /[a-zA-Z0-9]/.test(after);
        const hasSpaceBefore = /\s/.test(before);
        const hasSpaceAfter = /\s/.test(after);
        
        if (isBreakingWord && !hasSpaceBefore && !hasSpaceAfter) {
          return ' ';
        }
        return '';
      });
      
      cleanSummary = cleanSummary
        .replace(/<[^>]*>/g, '')
        .replace(/\s{3,}/g, ' ') // Replace 3+ spaces with single space
        .trim();

      // Find the original user query (previous message)
      const messageIndex = messages.findIndex(m => m.id === message.id);
      const userQuery = messageIndex > 0 && messages[messageIndex - 1]?.sender === 'user' 
        ? messages[messageIndex - 1].text 
        : 'AI Response';

      // Prepare request body
      const vizData = visualizations[message.id] || null;
      console.log('💾 [SAVE SUMMARY] Visualization data:', {
        messageId: message.id,
        hasViz: !!vizData,
        vizType: vizData ? (Array.isArray(vizData) ? 'array' : 'object') : 'none',
        vizData: vizData
      });
      
      const requestBody = {
        summary: cleanSummary,
        originalQuery: userQuery,
        visualizationData: vizData,
        title: `Summary: ${userQuery.substring(0, 50)}${userQuery.length > 50 ? '...' : ''}`
      };

      const token = localStorage.getItem('token');
      const response = await fetch(`${SERVER_URL}/api/v1/saved-summaries`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (data.success) {
        setSavedMessageIds(prev => new Set([...prev, message.id]));
        toast.success('✅ Summary saved successfully!', { duration: 3000 });
      } else {
        throw new Error(data.message || 'Failed to save summary');
      }
    } catch (error) {
      console.error('Error saving summary:', error);
      toast.error(`❌ Failed to save summary: ${error.message}`, { duration: 3000 });
    } finally {
      setSavingMessageId(null);
    }
  };

  // Handle send message
  const handleSendMessage = async () => {
    // Stop voice recognition if active
    if (isListening) {
      stopVoiceRecognition();
    }

    // Clean up any interim text markers
    const cleanText = inputValue.replace(/\s*\[Listening\.\.\.\]\s*$/, '').trim();
    if (!cleanText || isLoading) return;

    const userMessage = {
      id: Date.now(),
      text: cleanText,
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setSearchStatus('');
    // Note: Don't clear visualizations here - they are keyed by message ID, so old visualizations should persist

    // Stream chat response
    await streamChatResponse(cleanText);
  };

  // Voice recognition handlers
  const startVoiceRecognition = () => {
    if (!recognitionRef.current) {
      toast.error('Speech recognition is not supported in your browser.');
      return;
    }
    
    if (isListening) {
      // Stop if already listening
      stopVoiceRecognition();
      return;
    }
    
    try {
      // Clear input field for new recording
      setInputValue('');
      recognitionRef.current.start();
    } catch (error) {
      console.error('Error starting voice recognition:', error);
      toast.error('Failed to start voice recognition. Please try again.');
    }
  };

  const stopVoiceRecognition = () => {
    if (recognitionRef.current && isListening) {
      try {
        recognitionRef.current.stop();
      } catch (error) {
        console.error('Error stopping voice recognition:', error);
      }
    }
  };

  // Stop streaming
  const handleStopStream = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsLoading(false);
      setSearchStatus('');
      toast('Response stopped');
      
      // Mark last message as complete (not streaming anymore)
      setMessages((prev) =>
        prev.map((msg, index) =>
          index === prev.length - 1 && msg.isStreaming
            ? { ...msg, isStreaming: false }
            : msg
        )
      );
    }
  };

  // Stream chat response using SSE
  const streamChatResponse = async (query) => {
    try {
      // Create new abort controller for this stream
      abortControllerRef.current = new AbortController();
      
      // Prepare conversation history from existing messages
      const conversationHistory = messages.map(msg => ({
        role: msg.sender === 'user' ? 'user' : 'assistant',
        content: msg.text,
      }));
      
      const response = await fetch(`${SERVER_URL}/api/v1/chat/embeddings/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ 
          message: query,
          conversationHistory: conversationHistory,
          visualAnswers: !!visualAnswers,
        }),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        if (errData?.error?.code === 'AI_LIMIT_REACHED') {
          setShowAILimitModal(true);
          setIsLoading(false);
          return;
        }
        throw new Error('Failed to fetch');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      
      const botMessageId = Date.now() + 1;
      let botMessageCreated = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              console.log('📨 [SSE]', data.type, data.text ? `text: "${data.text.substring(0, 50)}..."` : '');
              
              if (data.type === 'status') {
                setSearchStatus(data.message);
                toast.success(data.message, { duration: 2000, icon: '🔍' });
              } 
              else if (data.type === 'content') {
                if (!botMessageCreated) {
                  console.log('🆕 [MESSAGE] Creating bot message, text length:', (data.text || '').length);
                  botMessageCreated = true;
                  setSearchStatus('');
                  setIsLoading(false);
                  const botMessage = {
                    id: botMessageId,
                    text: data.text || '',
                    sender: 'bot',
                    timestamp: new Date(),
                    isStreaming: true,
                  };
                  setMessages((prev) => [...prev, botMessage]);
                  console.log('✅ [MESSAGE] Bot message created');
                } else {
                  if (data.text) {
                    console.log('➕ [MESSAGE] Appending text, length:', data.text.length);
                  setMessages((prev) => 
                    prev.map(msg => 
                      msg.id === botMessageId 
                          ? { ...msg, text: (msg.text || '') + data.text }
                        : msg
                    )
                  );
                }
              } 
              }
              else if (data.type === 'visualization') {
                if (!botMessageCreated) {
                  botMessageCreated = true;
                  setSearchStatus('');
                  setIsLoading(false);
                  const botMessage = {
                    id: botMessageId,
                    text: '',
                    sender: 'bot',
                    timestamp: new Date(),
                    isStreaming: true,
                  };
                  setMessages((prev) => [...prev, botMessage]);
                }
                
                console.log('📊 [VISUALIZATION] Received visualization data:', {
                  chartType: data.chartType || data.type,
                  title: data.title,
                  dataLength: data.data?.length,
                  config: data.config,
                  insertPosition: data.insertPosition
                });
                setVisualizations((prev) => {
                  const messageViz = prev[botMessageId] || [];
                  const exists = messageViz.some(v => 
                    v.title === data.title && 
                    v.chartType === (data.chartType || data.type) &&
                    v.insertPosition === data.insertPosition
                  );
                  if (exists) return prev;
                  
                  return {
                    ...prev,
                    [botMessageId]: [...messageViz, {
                      chartType: data.chartType || data.type,
                      title: data.title,
                      data: data.data,
                      config: data.config,
                      columns: data.columns,
                      nameKey: data.nameKey,
                      valueKey: data.valueKey,
                      insertPosition: data.insertPosition || 0
                    }].sort((a, b) => (a.insertPosition || 0) - (b.insertPosition || 0))
                  };
                });
              }
              else if (data.type === 'content_cleanup' && data.removePattern) {
                setMessages((prev) => 
                  prev.map(msg => 
                    msg.id === botMessageId 
                      ? { ...msg, text: msg.text.replace(new RegExp(data.removePattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'), '') }
                      : msg
                  )
                );
              } 
              else if (data.type === 'done') {
                setIsLoading(false);
                setSearchStatus('');
                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === botMessageId ? { ...msg, isStreaming: false } : msg
                  )
                );
                window.dispatchEvent(new Event('ai-credit-used'));
              }
              else if (data.type === 'action_buttons') {
                setActionButtons((prev) => ({
                  ...prev,
                  [botMessageId]: data.buttons || [],
                }));
              }
              else if (data.type === 'pdf_metadata') {
                setPdfMetadata((prev) => ({
                  ...prev,
                  [botMessageId]: {
                    hasPdfDownload: data.hasPdfDownload,
                    reportType: data.reportType,
                    query: data.query
                  }
                }));
              }
              else if (data.type === 'error') {
                toast.error(data.message || 'An error occurred');
                setIsLoading(false);
                setSearchStatus('');
              }
            } catch (e) {
              console.error('Failed to parse SSE data:', e);
            }
          }
        }
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Stream stopped by user');
        // Toast already shown in handleStopStream
      } else {
        console.error('Stream error:', error);
        toast.error('Failed to get response');
      }
      setIsLoading(false);
      setSearchStatus('');
      abortControllerRef.current = null;
    }
  };

  // Handle action button click - directly send message
  const handleActionButtonClick = async (query) => {
    if (!query.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      text: query,
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setSearchStatus('');
    // Note: Don't clear visualizations here - they are keyed by message ID, so old visualizations should persist

    // Stream chat response
    await streamChatResponse(query);
  };

  // Handle textarea resize
  const handleInput = (e) => {
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 200) + 'px';
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const hasMessages = messages.length > 0;

  // Example prompts for empty state
  const examplePrompts = [
    { icon: '📊', text: 'Show me today\'s productivity summary', query: 'Show me today\'s productivity summary' },
    { icon: '🏆', text: 'Who were the most productive last week?', query: 'Who were the most productive team members last week?' },
    { icon: '❌', text: 'Who was inactive yesterday?', query: 'List all users who were inactive or absent yesterday' },
    { icon: '💡', text: 'How can I improve team productivity?', query: 'How can I improve my organization\'s productivity and performance?' },
  ];

  return (
    <PageAnimationWrapper className="min-h-full h-full flex flex-col transition-colors duration-300 bg-gray-50 dark:bg-[#1a1a1a]">
      {/* Main Container */}
      <div className="flex-1 flex flex-col overflow-hidden transition-colors duration-300 bg-gray-50 dark:bg-[#1a1a1a]" style={{ height: 'calc(100vh - 60px)' }}>
        
        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto transition-colors duration-300 bg-gray-50 dark:bg-[#1a1a1a]">
          <div className="max-w-4xl mx-auto px-4 py-6">
            {/* AI Usage Banner for free plan users */}
            <AIUsageBanner />

            {/* Empty State */}
            {!hasMessages && (
      <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="flex flex-col items-center justify-center min-h-[60vh]"
              >
                {/* Logo */}
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.1, duration: 0.5 }}
                  className="mb-8"
                >
                  <LogoIcon size={64} />
                  </motion.div>
                  
                {/* Title */}
                  <motion.h1
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.4 }}
                  className="text-3xl font-semibold mb-3 text-gray-900 dark:text-white"
                  >
                  How can I help you today?
                  </motion.h1>
                  
                {/* Subtitle */}
                  <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, duration: 0.4 }}
                  className="text-base mb-12 text-gray-600 dark:text-gray-400"
                  >
                  Ask me anything about your team's productivity and work patterns
                  </motion.p>

                {/* Example Prompts */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.4 }}
                  className="grid grid-cols-1 md:grid-cols-2 gap-3 w-full max-w-2xl"
                >
                  {examplePrompts.map((prompt, index) => (
                    <motion.button
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 + index * 0.1, duration: 0.3 }}
                      onClick={() => handleActionButtonClick(prompt.query)}
                      disabled={isLoading}
                      className={`group flex items-center gap-3 p-4 rounded-xl text-left transition-all duration-200 bg-white hover:bg-gray-50 border border-gray-200 hover:border-gray-300 shadow-sm hover:shadow dark:bg-[#2d2d2d] dark:hover:bg-[#353535] dark:border-[#3d3d3d] dark:hover:border-[#4d4d4d] ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <span className="text-2xl">{prompt.icon}</span>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                        {prompt.text}
                      </span>
                    </motion.button>
                  ))}
                </motion.div>
              </motion.div>
            )}

            {/* Messages */}
            {hasMessages && (
              <div className="space-y-6 pb-4">
                  <AnimatePresence>
                  {messages.map((message) => (
                    <motion.div
                      key={message.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                      onMouseEnter={() => setHoveredMessageId(message.id)}
                      onMouseLeave={() => setHoveredMessageId(null)}
                        className={`flex gap-4 ${
                        message.sender === 'user' ? 'justify-end' : 'justify-start'
                      }`}
                    >
                      {/* Bot Avatar */}
                      {message.sender === 'bot' && (
                        <div className="flex-shrink-0">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center bg-gray-100 dark:bg-[#2d2d2d]">
                            <LogoIcon size={24} />
                          </div>
                        </div>
                      )}

                      {/* Message Content */}
                      <div className={`flex-1 ${visualAnswers ? 'max-w-4xl' : 'max-w-3xl'} ${message.sender === 'user' ? 'flex justify-end' : ''}`}>
                        {/* Message Bubble */}
                        <div className={`${
                          message.sender === 'user'
                            ? 'bg-gray-200 dark:bg-[#2d2d2d] text-gray-900 dark:text-gray-100'
                            : 'bg-transparent'
                        } ${message.sender === 'user' ? 'rounded-2xl px-4 py-3 inline-block' : 'w-full'}`}>
                          
                          {/* Visual answers mode — same stream, graphic layout */}
                          {message.sender === 'bot' && visualAnswers ? (
                            <AskTgVisualAnswer
                              text={message.text}
                              visualizations={visualizations[message.id] || []}
                              isDark={isDark}
                              isStreaming={!!message.isStreaming}
                            />
                          ) : message.sender === 'bot' && visualizations[message.id] && Array.isArray(visualizations[message.id]) && visualizations[message.id].length > 0 && !message.isStreaming ? (
                            // Full inline rendering - only when streaming is complete
                            (() => {
                              const vizList = visualizations[message.id];
                              const originalText = message.text;
                              
                              const removeJsonVisualizations = (text) => {
                                if (!text) return text;
                                let cleaned = text;
                                
                                // Remove <VISUALIZATION> tags more carefully to preserve word boundaries
                                cleaned = cleaned.replace(/<VISUALIZATION>[\s\S]*?<\/VISUALIZATION>/gi, (match, offset, string) => {
                                  const before = string.substring(Math.max(0, offset - 1), offset);
                                  const after = string.substring(offset + match.length, offset + match.length + 1);
                                  const isBreakingWord = /[a-zA-Z0-9]/.test(before) && /[a-zA-Z0-9]/.test(after);
                                  const hasSpaceBefore = /\s/.test(before);
                                  const hasSpaceAfter = /\s/.test(after);
                                  
                                  if (isBreakingWord && !hasSpaceBefore && !hasSpaceAfter) {
                                    return ' ';
                                  }
                                  return '';
                                });
                                
                                // Aggressive JSON removal with brace counting for any leaked JSON
                                let maxPasses = 5;
                                for (let pass = 0; pass < maxPasses; pass++) {
                                  const beforeLength = cleaned.length;
                                  
                                  // Find JSON objects with "type" field
                                  const jsonPattern = /\{\s*"type"\s*:\s*"(line|bar|pie|table)"/gi;
                                  let match = jsonPattern.exec(cleaned);
                                  
                                  if (match) {
                                    const startPos = match.index;
                                    let braceCount = 0;
                                    let jsonEnd = startPos;
                                    let inString = false;
                                    let escapeNext = false;
                                    
                                    // Count braces to find complete JSON object
                                    for (let i = startPos; i < cleaned.length; i++) {
                                      const char = cleaned[i];
                                      if (escapeNext) {
                                        escapeNext = false;
                                        continue;
                                      }
                                      if (char === '\\') {
                                        escapeNext = true;
                                        continue;
                                      }
                                      if (char === '"') {
                                        inString = !inString;
                                        continue;
                                      }
                                      if (!inString) {
                                        if (char === '{') braceCount++;
                                        if (char === '}') {
                                          braceCount--;
                                          if (braceCount === 0) {
                                            jsonEnd = i + 1;
                                            break;
                                          }
                                        }
                                      }
                                    }
                                    
                                    if (jsonEnd > startPos) {
                                      // Check if breaking a word before removing
                                      const before = cleaned.substring(Math.max(0, startPos - 1), startPos);
                                      const after = cleaned.substring(jsonEnd, jsonEnd + 1);
                                      const isBreakingWord = /[a-zA-Z0-9]/.test(before) && /[a-zA-Z0-9]/.test(after);
                                      const hasSpaceBefore = /\s/.test(before);
                                      const hasSpaceAfter = /\s/.test(after);
                                      
                                      // Remove the JSON object, preserving word boundaries
                                      const replacement = (isBreakingWord && !hasSpaceBefore && !hasSpaceAfter) ? ' ' : '';
                                      cleaned = cleaned.substring(0, startPos) + replacement + cleaned.substring(jsonEnd);
                                    }
                                  }
                                  
                                  // If no changes, break
                                  if (cleaned.length === beforeLength) break;
                                }
                                
                                // Clean up extra whitespace
                                cleaned = cleaned.replace(/\n\s*\n\s*\n+/g, '\n\n');
                                cleaned = cleaned.replace(/\s{3,}/g, ' '); // Replace 3+ spaces with single space
                                
                                return cleaned.trim();
                              };
                              
                              const cleanText = removeJsonVisualizations(originalText);
                              const segments = [];
                              let lastIndex = 0;
                              
                              vizList.forEach((viz, index) => {
                                let originalPos = viz.insertPosition || 0;
                                // Adjust to word boundary to prevent word cutting
                                originalPos = adjustToWordBoundary(originalText, originalPos);
                                const textBeforeViz = originalText.substring(lastIndex, originalPos);
                                const cleanedTextBeforeViz = removeJsonVisualizations(textBeforeViz);
                                
                                if (cleanedTextBeforeViz.trim()) {
                                  segments.push({
                                    type: 'text',
                                    content: cleanedTextBeforeViz
                                  });
                                }
                                
                                segments.push({
                                  type: 'visualization',
                                  data: viz,
                                  index: index
                                });
                                
                                lastIndex = originalPos;
                              });
                              
                              const remainingOriginalText = originalText.substring(lastIndex);
                              const cleanedRemaining = removeJsonVisualizations(remainingOriginalText);
                              if (cleanedRemaining.trim()) {
                                segments.push({
                                  type: 'text',
                                  content: cleanedRemaining
                                });
                              }
                              
                              if (segments.length === 0 || segments.every(s => s.type === 'visualization')) {
                                const finalCleaned = removeJsonVisualizations(originalText);
                                if (finalCleaned.trim()) {
                                  segments.unshift({
                                    type: 'text',
                                    content: finalCleaned
                                  });
                                }
                              }
                              
                              return (
                                <>
                                  {segments.map((segment, segIndex) => {
                                    if (segment.type === 'text') {
                                      return (
                                        <div
                                          key={`text-${segIndex}`}
                                          className="text-base leading-relaxed whitespace-pre-wrap break-words text-gray-900 dark:text-gray-100"
                                          dangerouslySetInnerHTML={{
                                            __html: segment.content
                                              .replace(/^###?\s+/gm, '')
                                              .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                              .replace(/\*(.*?)\*/g, '<em>$1</em>')
                                              .replace(/\n/g, '<br />')
                                          }}
                                        />
                                      );
                                    } else {
                                      return (
                                        <motion.div
                                          key={`viz-${segment.index}`}
                                          initial={{ opacity: 0, y: 10 }}
                                          animate={{ opacity: 1, y: 0 }}
                                          transition={{ duration: 0.3, delay: segment.index * 0.1 }}
                                          className="my-4 w-full"
                                        >
                                          <VisualizationRenderer 
                                            data={segment.data} 
                                            isDark={isDark}
                                          />
                                        </motion.div>
                                      );
                                    }
                                  })}
                                </>
                              );
                            })()
                          ) : message.sender === 'bot' && visualizations[message.id] && Array.isArray(visualizations[message.id]) && visualizations[message.id].length > 0 && message.isStreaming ? (
                            // Inline rendering during streaming - visualizations appear in correct positions
                            (() => {
                              const vizList = visualizations[message.id];
                              const originalText = message.text;
                              
                              const removeJsonVisualizations = (text) => {
                                if (!text) return text;
                                let cleaned = text;
                                
                                // Remove <VISUALIZATION> tags more carefully to preserve word boundaries
                                cleaned = cleaned.replace(/<VISUALIZATION>[\s\S]*?<\/VISUALIZATION>/gi, (match, offset, string) => {
                                  const before = string.substring(Math.max(0, offset - 1), offset);
                                  const after = string.substring(offset + match.length, offset + match.length + 1);
                                  const isBreakingWord = /[a-zA-Z0-9]/.test(before) && /[a-zA-Z0-9]/.test(after);
                                  const hasSpaceBefore = /\s/.test(before);
                                  const hasSpaceAfter = /\s/.test(after);
                                  
                                  if (isBreakingWord && !hasSpaceBefore && !hasSpaceAfter) {
                                    return ' ';
                                  }
                                  return '';
                                });
                                
                                // Aggressive JSON removal with brace counting for any leaked JSON
                                let maxPasses = 5;
                                for (let pass = 0; pass < maxPasses; pass++) {
                                  const beforeLength = cleaned.length;
                                  
                                  // Find JSON objects with "type" field
                                  const jsonPattern = /\{\s*"type"\s*:\s*"(line|bar|pie|table)"/gi;
                                  let match = jsonPattern.exec(cleaned);
                                  
                                  if (match) {
                                    const startPos = match.index;
                                    let braceCount = 0;
                                    let jsonEnd = startPos;
                                    let inString = false;
                                    let escapeNext = false;
                                    
                                    // Count braces to find complete JSON object
                                    for (let i = startPos; i < cleaned.length; i++) {
                                      const char = cleaned[i];
                                      if (escapeNext) {
                                        escapeNext = false;
                                        continue;
                                      }
                                      if (char === '\\') {
                                        escapeNext = true;
                                        continue;
                                      }
                                      if (char === '"') {
                                        inString = !inString;
                                        continue;
                                      }
                                      if (!inString) {
                                        if (char === '{') braceCount++;
                                        if (char === '}') {
                                          braceCount--;
                                          if (braceCount === 0) {
                                            jsonEnd = i + 1;
                                            break;
                                          }
                                        }
                                      }
                                    }
                                    
                                    if (jsonEnd > startPos) {
                                      // Check if breaking a word before removing
                                      const before = cleaned.substring(Math.max(0, startPos - 1), startPos);
                                      const after = cleaned.substring(jsonEnd, jsonEnd + 1);
                                      const isBreakingWord = /[a-zA-Z0-9]/.test(before) && /[a-zA-Z0-9]/.test(after);
                                      const hasSpaceBefore = /\s/.test(before);
                                      const hasSpaceAfter = /\s/.test(after);
                                      
                                      // Remove the JSON object, preserving word boundaries
                                      const replacement = (isBreakingWord && !hasSpaceBefore && !hasSpaceAfter) ? ' ' : '';
                                      cleaned = cleaned.substring(0, startPos) + replacement + cleaned.substring(jsonEnd);
                                    }
                                  }
                                  
                                  // If no changes, break
                                  if (cleaned.length === beforeLength) break;
                                }
                                
                                // Clean up extra whitespace
                                cleaned = cleaned.replace(/\n\s*\n\s*\n+/g, '\n\n');
                                cleaned = cleaned.replace(/\s{3,}/g, ' '); // Replace 3+ spaces with single space
                                
                                return cleaned.trim();
                              };
                              
                              // Sort visualizations by insertPosition
                              const sortedVizList = [...vizList].sort((a, b) => (a.insertPosition || 0) - (b.insertPosition || 0));
                              
                              const segments = [];
                              let lastIndex = 0;
                              
                              sortedVizList.forEach((viz, index) => {
                                const originalPos = viz.insertPosition || 0;
                                // Clamp position to valid range (text might still be streaming)
                                const clampedPos = Math.min(Math.max(0, originalPos), originalText.length);
                                // Adjust to word boundary to prevent word cutting
                                const adjustedPos = adjustToWordBoundary(originalText, clampedPos);
                                
                                const textBeforeViz = originalText.substring(lastIndex, adjustedPos);
                                const cleanedTextBeforeViz = removeJsonVisualizations(textBeforeViz);
                                
                                if (cleanedTextBeforeViz.trim()) {
                                  segments.push({
                                    type: 'text',
                                    content: cleanedTextBeforeViz
                                  });
                                }
                                
                                segments.push({
                                  type: 'visualization',
                                  data: viz,
                                  index: index
                                });
                                
                                lastIndex = adjustedPos;
                              });
                              
                              // Add remaining text after last visualization
                              const remainingOriginalText = originalText.substring(lastIndex);
                              const cleanedRemaining = removeJsonVisualizations(remainingOriginalText);
                              if (cleanedRemaining.trim()) {
                                segments.push({
                                  type: 'text',
                                  content: cleanedRemaining
                                });
                              }
                              
                              // If no text segments but we have visualizations, add text at start
                              if (segments.length === 0 || segments.every(s => s.type === 'visualization')) {
                                const finalCleaned = removeJsonVisualizations(originalText);
                                if (finalCleaned.trim()) {
                                  segments.unshift({
                                    type: 'text',
                                    content: finalCleaned
                                  });
                                }
                              }
                              
                              return (
                                <>
                                  {segments.map((segment, segIndex) => {
                                    if (segment.type === 'text') {
                                      return (
                                        <div
                                          key={`text-${segIndex}`}
                                          className="text-base leading-relaxed whitespace-pre-wrap break-words text-gray-900 dark:text-gray-100"
                                          dangerouslySetInnerHTML={{
                                            __html: segment.content
                                              .replace(/^###?\s+/gm, '')
                                              .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                              .replace(/\*(.*?)\*/g, '<em>$1</em>')
                                              .replace(/\n/g, '<br />')
                                          }}
                                        />
                                      );
                                    } else {
                                      return (
                                        <motion.div
                                          key={`viz-${segment.index}`}
                                          initial={{ opacity: 0, y: 10 }}
                                          animate={{ opacity: 1, y: 0 }}
                                          transition={{ duration: 0.3, delay: segment.index * 0.1 }}
                                          className="my-4 w-full"
                                        >
                                          <VisualizationRenderer 
                                            data={segment.data} 
                                            isDark={isDark}
                                          />
                                        </motion.div>
                                      );
                                    }
                                  })}
                                  {/* Show streaming cursor indicator */}
                                  <span className="inline-block w-2 h-4 ml-1 bg-purple-500 animate-pulse" />
                                </>
                              );
                            })()
                          ) : (
                            // Plain text
                            <div 
                              className="text-base leading-relaxed whitespace-pre-wrap break-words text-gray-900 dark:text-gray-100"
                              dangerouslySetInnerHTML={{
                                __html: (() => {
                                  let cleaned = message.text;
                                  
                                  // Remove visualization tags more carefully to preserve word boundaries
                                  cleaned = cleaned.replace(/<VISUALIZATION>[\s\S]*?<\/VISUALIZATION>/gi, (match, offset, string) => {
                                    const before = string.substring(Math.max(0, offset - 1), offset);
                                    const after = string.substring(offset + match.length, offset + match.length + 1);
                                    const isBreakingWord = /[a-zA-Z0-9]/.test(before) && /[a-zA-Z0-9]/.test(after);
                                    const hasSpaceBefore = /\s/.test(before);
                                    const hasSpaceAfter = /\s/.test(after);
                                    
                                    if (isBreakingWord && !hasSpaceBefore && !hasSpaceAfter) {
                                      return ' ';
                                    }
                                    return '';
                                  });
                                  
                                  cleaned = cleaned.replace(/\{\s*"type"\s*:\s*"(line|bar|pie|table)"[\s\S]{0,15000}?\}/gi, (match, offset, string) => {
                                    const before = string.substring(Math.max(0, offset - 1), offset);
                                    const after = string.substring(offset + match.length, offset + match.length + 1);
                                    const isBreakingWord = /[a-zA-Z0-9]/.test(before) && /[a-zA-Z0-9]/.test(after);
                                    const hasSpaceBefore = /\s/.test(before);
                                    const hasSpaceAfter = /\s/.test(after);
                                    
                                    if (isBreakingWord && !hasSpaceBefore && !hasSpaceAfter) {
                                      return ' ';
                                    }
                                    return '';
                                  });
                                  
                                  cleaned = cleaned.replace(/\s{3,}/g, ' '); // Replace 3+ spaces with single space
                                  
                                  return cleaned
                                    .replace(/^###?\s+/gm, '')
                                    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                    .replace(/\*(.*?)\*/g, '<em>$1</em>')
                                    .replace(/\n/g, '<br />');
                                })()
                              }}
                            />
                          )}
                        </div>

                        {/* Action Buttons - Always visible for bot messages */}
                        {message.sender === 'bot' && !message.isStreaming && (
                            <motion.div 
                              initial={{ opacity: 1 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.15 }}
                              className="flex items-center gap-2 mt-2"
                            >
                            <button
                                onClick={() => handleCopyMessage(message.id, message.text)}
                              className="p-1.5 rounded-lg transition-all text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:text-gray-500 dark:hover:text-gray-300 dark:hover:bg-[#2d2d2d]"
                                title="Copy message"
                              >
                                {copiedMessageId === message.id ? (
                                <Check className="h-4 w-4" />
                              ) : (
                                <Copy className="h-4 w-4" />
                              )}
                            </button>
                            
                            {/* PDF Download Button */}
                            {pdfMetadata[message.id]?.hasPdfDownload && (
                              <button
                                onClick={() => handleDownloadPDF(message)}
                                className="p-1.5 rounded-lg transition-all hover:bg-gray-100 dark:hover:bg-[#2d2d2d] flex items-center group"
                                title="Download as PDF"
                              >
                                <img 
                                  src="/free-pdf-download-icon-2617-thumb.webp" 
                                  alt="PDF" 
                                  className="h-6 w-6 object-contain flex-shrink-0"
                                  style={{ 
                                    imageRendering: 'crisp-edges',
                                    display: 'block'
                                  }}
                                />
                              </button>
                            )}
                            
                            {/* Save Summary Button - Commented out */}
                            {/* <button
                                onClick={() => handleSaveSummary(message)}
                                disabled={savingMessageId === message.id}
                              className="p-1.5 rounded-lg transition-all text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:text-gray-500 dark:hover:text-gray-300 dark:hover:bg-[#2d2d2d] disabled:opacity-50 disabled:cursor-not-allowed"
                                title={savedMessageIds.has(message.id) ? "Summary saved" : "Save summary"}
                              >
                                {savingMessageId === message.id ? (
                                  <RotateCcw className="h-4 w-4 animate-spin" />
                                ) : savedMessageIds.has(message.id) ? (
                                  <div className="relative">
                                    <Bookmark className="h-4 w-4 text-green-500 dark:text-green-400 fill-current" />
                                    <Check className="h-3 w-3 text-white dark:text-green-900 absolute top-0 left-0.5" />
                                  </div>
                              ) : (
                                <Bookmark className="h-4 w-4" />
                              )}
                            </button> */}
                            </motion.div>
                        )}
                            
                        {/* Suggested Actions */}
                        {message.sender === 'bot' && actionButtons[message.id] && actionButtons[message.id].length > 0 && !message.isStreaming && (
                                <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2, duration: 0.3 }}
                            className="flex flex-wrap gap-2 mt-3"
                                >
                                {actionButtons[message.id].map((button, idx) => (
                              <button
                                    key={idx}
                                    onClick={() => handleActionButtonClick(button.query)}
                                    disabled={isLoading}
                                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-all bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 shadow-sm dark:bg-[#2d2d2d] dark:hover:bg-[#353535] dark:text-gray-300 dark:border-[#3d3d3d] ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                              >
                                <Sparkles className="h-3.5 w-3.5" />
                                        <span>{button.label}</span>
                              </button>
                                ))}
                                </motion.div>
                        )}
                      </div>

                      {/* User Avatar */}
                      {message.sender === 'user' && (
                        <div className="flex-shrink-0">
                          {resolveUserAvatar(user) ? (
                            <img src={resolveUserAvatar(user)} alt="" className="w-8 h-8 rounded-full object-cover" />
                          ) : (
                          <div className="w-8 h-8 rounded-full flex items-center justify-center bg-purple-500 dark:bg-purple-600">
                            <User className="h-5 w-5 !text-white" />
                          </div>
                          )}
                        </div>
                      )}
                    </motion.div>
                  ))}
            </AnimatePresence>

            {/* Typing Indicator */}
            {isLoading && (
              <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="flex gap-4"
                  >
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center bg-gray-100 dark:bg-[#2d2d2d]">
                        <LogoIcon size={24} />
                      </div>
                    </div>
                    <div className="flex items-center gap-1 py-3">
                  <motion.div 
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ duration: 0.6, repeat: Infinity, delay: 0 }}
                        className={`w-2 h-2 rounded-full ${isDark ? 'bg-gray-500' : 'bg-gray-400'}`}
                      />
                  <motion.div 
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ duration: 0.6, repeat: Infinity, delay: 0.2 }}
                        className={`w-2 h-2 rounded-full ${isDark ? 'bg-gray-500' : 'bg-gray-400'}`}
                      />
                        <motion.div
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ duration: 0.6, repeat: Infinity, delay: 0.4 }}
                        className={`w-2 h-2 rounded-full ${isDark ? 'bg-gray-500' : 'bg-gray-400'}`}
                      />
                      {searchStatus && (
                        <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                          {searchStatus}
                        </span>
                      )}
                    </div>
              </motion.div>
            )}

                <div ref={messagesEndRef} />
              </div>
            )}
          </div>
        </div>

        {/* Input Area - Fixed to Bottom */}
        <div className="border-t transition-colors duration-300 bg-white dark:bg-[#1a1a1a] border-gray-200 dark:border-[#2d2d2d]">
          <div className="max-w-4xl mx-auto px-4 py-4">
            {/* Voice Recording Indicator */}
            {isListening && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className={`mb-3 px-4 py-3 rounded-xl flex items-center gap-3 ${
                  isDark
                    ? 'bg-red-500/10 border border-red-500/20'
                    : 'bg-red-50 border border-red-200'
                }`}
              >
                <motion.div
                  animate={{
                    scale: [1, 1.2, 1],
                  }}
                  transition={{
                    duration: 1,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                  className={`w-3 h-3 rounded-full ${
                    isDark ? 'bg-red-500' : 'bg-red-600'
                  }`}
                />
                <span className={`text-sm font-medium ${
                  isDark ? 'text-red-400' : 'text-red-600'
                }`}>
                  Listening... Speak your question
                </span>
                <button
                  onClick={stopVoiceRecognition}
                  className={`ml-auto px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                    isDark
                      ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                      : 'bg-red-100 text-red-600 hover:bg-red-200'
                  }`}
                >
                  Stop
                </button>
              </motion.div>
            )}

            <div className="flex items-end gap-3">
              {/* Textarea */}
              <div className="flex-1 relative">
                <textarea
                  ref={textareaRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onInput={handleInput}
                  onKeyDown={handleKeyPress}
                  placeholder="Type your question..."
                  disabled={isLoading || isListening}
                  rows={1}
                  className={`w-full px-4 py-3 rounded-xl resize-none transition-all duration-200 focus:outline-none bg-gray-50 dark:bg-[#2d2d2d] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 border transition-colors ${
                    isListening 
                      ? 'border-red-300 dark:border-red-500/30' 
                      : 'border-gray-200 dark:border-[#3d3d3d] focus:border-gray-300 dark:focus:border-[#4d4d4d]'
                  } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  style={{ maxHeight: '200px', minHeight: '48px' }}
                />
              </div>

              {/* Voice Input Button */}
              {recognitionSupported && (
                <motion.button
                  type="button"
                  onClick={startVoiceRecognition}
                  disabled={isLoading}
                  className={`relative flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-200 ${
                    isListening
                      ? isDark
                        ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30'
                        : 'bg-red-500/20 text-red-600 hover:bg-red-500/30 border border-red-300'
                      : isDark
                      ? 'bg-gray-100 dark:bg-[#2d2d2d] text-gray-400 dark:text-gray-500 hover:bg-gray-200 dark:hover:bg-[#353535]'
                      : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                  } ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  whileHover={!isLoading && !isListening ? { scale: 1.05 } : {}}
                  whileTap={!isLoading ? { scale: 0.95 } : {}}
                  title={isListening ? 'Stop recording' : 'Start voice input'}
                >
                  {isListening ? (
                    <>
                      {/* Pulsing animation */}
                      <motion.div
                        className={`absolute inset-0 rounded-xl ${
                          isDark ? 'bg-red-500/30' : 'bg-red-500/30'
                        }`}
                        animate={{
                          scale: [1, 1.2, 1],
                          opacity: [0.5, 0.8, 0.5],
                        }}
                        transition={{
                          duration: 1.5,
                          repeat: Infinity,
                          ease: 'easeInOut',
                        }}
                      />
                      <Mic className="h-5 w-5 relative z-10" />
                    </>
                  ) : (
                    <Mic className="h-5 w-5" />
                  )}
                </motion.button>
              )}

              {/* Send/Stop Button */}
              <button
                onClick={isLoading ? handleStopStream : handleSendMessage}
                disabled={(!inputValue.trim() && !isLoading) || isListening}
                className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-200 ${
                  isLoading
                    ? 'bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700 !text-white cursor-pointer'
                        : inputValue.trim() && !isListening
                      ? 'bg-purple-500 hover:bg-purple-600 dark:bg-purple-600 dark:hover:bg-purple-700 !text-white'
                      : 'bg-gray-100 dark:bg-[#2d2d2d] text-gray-400 dark:text-gray-600 cursor-not-allowed'
                }`}
                title={isLoading ? 'Stop generation' : 'Send message'}
              >
                {isLoading ? (
                  <StopCircle className="h-5 w-5" />
                ) : (
                  <Send className="h-5 w-5" />
                )}
              </button>
            </div>

            {/* Helper Text */}
            <p className="text-xs mt-2 text-center text-gray-400 dark:text-gray-500">
              Press Shift + Enter for new line
            </p>
          </div>
        </div>
      </div>

      <AILimitModal isOpen={showAILimitModal} onClose={() => setShowAILimitModal(false)} />
    </PageAnimationWrapper>
  );
};

export default AISearch;
